import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    TextField,
    Paper,
    Typography,
    IconButton,
    InputAdornment,
    FormControlLabel,
    Checkbox,
    Button
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { createUser } from '../../Actions/thunks';
import { clearClaimProperty } from '../../Actions/PropertyActions';
import { baseEndpoint, isMobile } from '../../App';
import {
    CreditCardInput,
    SquarePaymentsForm
} from 'react-square-web-payments-sdk';
import TermsDialog from '../TermsDialog';
import { Property } from '../../Models/Property';
const config = require("../../env.json")


interface SubscriptionRequest {
    sourceId: string;
    planId: string;
    email: string;
    firstName: string;
    lastName: string;
}

interface SquareTokenResponse {
    token?: string;
    status: string;
    details?: {
        billing?: {
            postalCode?: string;
            addressLine1?: string;
            locality?: string;
            administrativeDistrictLevel1?: string;
        }
    }
}

interface Props {
    userState: any;
    propertyState: {
        claimProperty: {
            property: Property | null;
            timestamp: number | null;
        } | null;
    };
    createUser: (value: any) => Promise<any>;
    clearClaimProperty: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
            margin: "15px",
            textAlign: "left",
            paddingBottom: "25px"
        },
        input: {
            backgroundColor: "white",
            '& label.Mui-focused': {
                color: '#0404fc',
                borderWidth: "3px",
                backgroundColor: "white"
            },
            '& .MuiInputLabel-outlined': {
                backgroundColor: "white",
                paddingRight: "10px",
                transform: "translate(14px, 13px) scale(1)"
            },
            '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
                transform: "translate(14px, -6px) scale(0.75)"
            },
            '& .MuiOutlinedInput-input': {
                paddingTop: isMobile === false ? "12px" : "10px",
                paddingBottom: isMobile === false ? "12px" : "10px"
            },
            '& .MuiOutlinedInput-root': {
                '& fieldset': {
                    borderColor: '#0404fc',
                    borderWidth: "3px"
                },
                '&:hover fieldset': {
                    borderColor: '#0404fc',
                    borderWidth: "3px"
                },
                '&.Mui-focused fieldset': {
                    borderColor: '#0404fc',
                    borderWidth: "3px"
                },
            },
        },
        inputError: {
            backgroundColor: "white",
            '& label.Mui-focused': {
                borderWidth: "3px"
            },
            '& .MuiInput-underline:after': {
                borderWidth: "3px"
            },
            '& .MuiInputLabel-outlined': {
                backgroundColor: "white",
                paddingRight: "10px",
                transform: "translate(14px, 13px) scale(1)"
            },
            '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
                transform: "translate(14px, -6px) scale(0.75)"
            },
            '& .MuiOutlinedInput-input': {
                paddingTop: "12px",
                paddingBottom: "12px"
            },
            '& .MuiOutlinedInput-root': {
                '& fieldset': {
                    borderWidth: "3px"
                },
                '&:hover fieldset': {
                    borderWidth: "3px"
                },
                '&.Mui-focused fieldset': {
                    borderWidth: "3px"
                },
            },
        }
    }),
);

function LandlordSignUp(props: Props) {
    const classes = useStyles();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const planId = params.get('plan');
    const planName = decodeURIComponent(params.get('name') || '');
    const [isLoading, setIsLoading] = useState(false);
    // Form state
    const [firstName, setFirstName] = useState<string>("");
    const [firstNameError, setFirstNameError] = useState<boolean>(false);
    const [lastName, setLastName] = useState<string>("");
    const [lastNameError, setLastNameError] = useState<boolean>(false);
    const [company, setCompany] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [confirmPass, setConfirmPass] = useState<string>("");
    const [confirmError, setConfirmError] = useState<boolean>(false);
    const [showPass, setShowPass] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
    const [showTermsDialog, setShowTermsDialog] = useState<boolean>(false);
    useEffect(() => {
        if (!planId) {
            window.location.href = '/signup/landlord/subscriptions';
        }
    }, [planId]);

    async function createAccount() {
        return await props.createUser({
            category: "landlord",
            firstName,
            lastName,
            company,
            email,
            password,
            subscriptionPlan: planId
        });
    }

    const handleSubscription = async (tokenResponse: SquareTokenResponse, buyer: any) => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            if (tokenResponse.status === "OK" && tokenResponse.token) {
                // Create user and subscription first
                const userResponse = await createAccount();
                console.log('User response:', userResponse); // Debug log

                if (!userResponse?.success) {
                    throw new Error(userResponse?.message || 'Failed to create user account');
                }

                const subscriptionRequest: SubscriptionRequest = {
                    sourceId: tokenResponse.token,
                    planId: planId!,
                    email,
                    firstName,
                    lastName
                };

                const subscriptionResponse = await fetch(baseEndpoint + 'users/subscription/create', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(subscriptionRequest)
                });

                if (!subscriptionResponse.ok) {
                    throw new Error('Failed to create subscription');
                }

                // Handle property claim if it exists and is not expired
                const EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutes
                const claimProperty = props.propertyState.claimProperty;
                
                if (claimProperty?.property && 
                    claimProperty.timestamp && 
                    Date.now() - claimProperty.timestamp < EXPIRATION_TIME) {
                    
                    try {
                        const claimResponse = await fetch(
                            baseEndpoint + "properties/claim/" + claimProperty.property.addresses[0].internalId, 
                            {
                                method: 'POST',
                                headers: {
                                    "Authorization": "Bearer " + userResponse.user.currentToken,
                                    'Content-Type': 'application/json',
                                },
                            }
                        );

                        if (!claimResponse.ok) {
                            console.error('Property claim failed, but account was created');
                        }
                    } catch (error) {
                        console.error('Error claiming property:', error);
                    }
                }

                window.location.href = '/landlordDashboard';
            } else {
                setErrorMessage("Payment processing failed. Please try again.");
            }
        } catch (error: any) {
            setErrorMessage("Failed to process payment. Please try again.");
            console.error("Subscription error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseTermsDialog = (accepted: boolean) => {
        setShowTermsDialog(false);
        if (accepted === true) {
            setTermsAccepted(true);
        }
    }
    return (
        <div style={{ flexGrow: 1 }}>
            <div style={{ backgroundColor: "#0404fc", borderWidth: "5px", height: "29px", marginRight: "17px", marginLeft: "17px" }} />
            <Paper className={classes.root}>
                <Typography style={{ marginLeft: "15px", marginRight: "15px" }} variant="h4">
                    Create Landlord Account
                </Typography>
                {planName && (
                    <Typography style={{ marginLeft: "15px", marginRight: "15px", marginTop: "10px" }} variant="h6">
                        Selected Plan: {planName}
                    </Typography>
                )}
                <form style={{ width: isMobile ? "85%" : "65%", margin: "15px auto 0" }}>
                    {props.userState.signupMessage && (
                        <Alert severity="error" style={{ marginBottom: "15px", padding: "0px 10px" }}>
                            <AlertTitle>Error</AlertTitle>
                            {props.userState.signupMessage}
                        </Alert>
                    )}

                    <div style={{ width: "100%", display: isMobile ? "block" : "flex", marginTop: "10px" }}>
                        <TextField
                            id="first-name"
                            label="First Name *"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            variant="outlined"
                            className={firstNameError ? classes.inputError : classes.input}
                            error={firstNameError}
                            helperText={firstNameError ? "**Required" : ""}
                            style={{ width: isMobile ? "100%" : "49%", marginRight: "2%" }}
                        />
                        <TextField
                            id="last-name"
                            label="Last Name *"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            variant="outlined"
                            className={lastNameError ? classes.inputError : classes.input}
                            error={lastNameError}
                            helperText={lastNameError ? "**Required" : ""}
                            style={{ width: isMobile ? "100%" : "49%", marginTop: isMobile ? "10px" : "0px" }}
                        />
                    </div>

                    <div style={{ width: "100%", display: "flex", marginTop: "10px" }}>
                        <TextField
                            id="company"
                            label="Company"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            variant="outlined"
                            className={classes.input}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div style={{ width: "100%", display: "flex", marginTop: "10px" }}>
                        <TextField
                            id="email"
                            label="Email Address *"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            variant="outlined"
                            className={emailError ? classes.inputError : classes.input}
                            error={!!emailError}
                            helperText={emailError}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div style={{ width: "100%", display: "flex", marginTop: "10px" }}>
                        <TextField
                            id="phone"
                            label="Phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            variant="outlined"
                            className={classes.input}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div style={{ width: "100%", display: "flex", marginTop: "10px" }}>
                        <TextField
                            id="password"
                            label="Password *"
                            value={password}
                            type={showPass ? "text" : "password"}
                            onChange={(e) => setPassword(e.target.value)}
                            variant="outlined"
                            className={passwordError ? classes.inputError : classes.input}
                            error={passwordError}
                            helperText={passwordError ? "**Must be at least 8 characters" : "Must be at least 8 characters"}
                            style={{ width: "100%" }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPass(!showPass)}>
                                            {showPass ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </div>

                    <div style={{ width: "100%", display: "flex", marginTop: "10px", marginBottom: "25px" }}>
                        <TextField
                            id="confirm"
                            label="Confirm Password *"
                            value={confirmPass}
                            type={showPass ? "text" : "password"}
                            onChange={(e) => setConfirmPass(e.target.value)}
                            variant="outlined"
                            className={confirmError ? classes.inputError : classes.input}
                            error={confirmError}
                            helperText={confirmError ? "**Passwords do not match" : ""}
                            style={{ width: "100%" }}
                        />
                    </div>
                    <div style={{ marginBottom: "20px" }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={termsAccepted}
                                    onChange={() => {
                                        if (!termsAccepted) {
                                            setShowTermsDialog(true);
                                        }
                                    }}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    I agree to the{' '}
                                    <Button
                                        color="primary"
                                        style={{ padding: '0 4px', minWidth: 0, textTransform: 'none' }}
                                        onClick={() => setShowTermsDialog(true)}
                                    >
                                        Terms and Conditions
                                    </Button>
                                </Typography>
                            }
                        />
                    </div>
                    <div style={{ marginTop: "25px", marginBottom: "25px" }}>
                        <SquarePaymentsForm
                            applicationId={config.REACT_APP_SQUARE_ID}
                            locationId={config.REACT_APP_LOCATION_ID}
                            cardTokenizeResponseReceived={(tokenResponse: SquareTokenResponse, buyer: any) => {
                                if (isLoading) {
                                    return;
                                }
                                let isValid = true;
                                var emailRegEx = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

                                if (firstName.trim() === "") {
                                    setFirstNameError(true);
                                    isValid = false;
                                }
                                if (lastName.trim() === "") {
                                    setLastNameError(true);
                                    isValid = false;
                                }
                                if (email.trim() === "") {
                                    setEmailError("**Email required");
                                    isValid = false;
                                } else if (!emailRegEx.test(email)) {
                                    setEmailError("**Invalid email");
                                    isValid = false;
                                }
                                if (password.trim().length < 8) {
                                    setPasswordError(true);
                                    isValid = false;
                                }
                                if (password !== confirmPass) {
                                    setConfirmError(true);
                                    isValid = false;
                                }
                                if (!termsAccepted) {
                                    setErrorMessage("Please accept the Terms and Conditions");
                                    isValid = false;
                                }

                                if (isValid) {
                                    handleSubscription(tokenResponse, buyer);
                                }
                            }}
                            createVerificationDetails={() => ({
                                amount: "100.00", // Replace with actual plan amount
                                billingContact: {
                                    familyName: lastName,
                                    givenName: firstName,
                                    email: email,
                                    countryCode: 'US'
                                },
                                currencyCode: 'USD',
                                intent: 'STORE',
                            })}
                        > 
                            
                            <CreditCardInput
                                text={isLoading ? "Processing..." : `Subscribe to ${planName}`}
                            />
                        </SquarePaymentsForm>
                    </div>

                    {errorMessage && (
                        <Alert severity="error" style={{ marginBottom: "15px" }}>
                            {errorMessage}
                        </Alert>
                    )}
                </form>
            </Paper>
            <TermsDialog showTermsDialog={showTermsDialog} onClose={handleCloseTermsDialog} />
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    userState: state.userState,
    propertyState: state.propertyState || { claimProperty: null }
});

const mapDispatchToProps = (dispatch: any) => ({
    createUser: (value: any) => dispatch(createUser(value)),
    clearClaimProperty: () => dispatch(clearClaimProperty())
});

export default connect(mapStateToProps, mapDispatchToProps)(LandlordSignUp);